import { Tag } from '@amzn/awscat-portfolio-assessment-types';
import { Box, SpaceBetween } from '@amzn/awsui-components-react';
import { FormattedDate } from 'react-intl';
import { Link } from 'react-router-dom';

import { toDetailedMonthAbvDate } from '../../common/DateDisplay';
import { constants } from '../../common/Localization';
import { ColumnDefinition, ColumnTypes } from '../../common/table/types';
import { getCell } from '../common/awsui-table-helper';
import { PortfolioViewModel } from './interface';

/**
 * Return a FormattedDate component with the format: Jun 1, 2022, 02:19 PM
 * @param dateToFormat 
 * @returns 
 */
const formattedDate = (dateToFormat: string): JSX.Element => {
    return (
        <FormattedDate value={dateToFormat}
            day='numeric'
            month='short'
            year='numeric'
            hour='2-digit'
            minute='2-digit'
            hour12
        />
    );
};

export const columnDefinitions: ColumnDefinition<PortfolioViewModel>[] = [
    {
        id: 'portfolioName',
        header: constants.PORTFOLIOS_NAME,
        cell: item => <Link to={`/portfolio/${item.portfolioId}/management/assets/servers`}>{item.portfolioName}</Link>,
        type: ColumnTypes.TEXT,
    },
    {
        id: 'accountName',
        header: constants.PORTFOLIOS_ACCOUNT_NAME,
        cell: e => e.accountName,
        type: ColumnTypes.TEXT,
    },
    {
        id: 'createdAt',
        header: constants.PORTFOLIOS_CREATED_AT,
        data: item => item.createdAt ? toDetailedMonthAbvDate(new Date(item.createdAt)) : '',
        cell: item => formattedDate(item.createdAt || ''),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'updatedAt',
        header: constants.PORTFOLIOS_UPDATED_AT,
        data: item => item.updatedAt ? toDetailedMonthAbvDate(new Date(item.updatedAt)) : '',
        cell: item => formattedDate(item.updatedAt || ''),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'updatedBy',
        header: constants.PORTFOLIOS_UPDATED_BY,
        cell: item => getCell(item.updatedBy, false, 'left'),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'tags',
        header: constants.TAGS,
        data: item => (item.tags?.map(tag => `${tag.key}:${tag.value}`) ?? []).join(','),
        cell: item => {
            const tags = item.tags?.map(tag => {
                return (<Box key={tag.key}>{`${tag.key}: ${tag.value}`}</Box>);
            });
            return <SpaceBetween size='s' direction='vertical'>{tags}</SpaceBetween>;
        },
        type: ColumnTypes.TEXT
    }
];

/**
 * Get the column definition list which is used to create a custom group filter
 * Each key present in the user's list of portfolio tags has a corresponding item
 * in the array so the user can filter on specific tag keys
 * @param portfolios 
 * @returns 
 */
export const getTagColumnDefinitions = (portfolios: PortfolioViewModel[]): ColumnDefinition<PortfolioViewModel>[] => {
    const allTags: Tag[] = portfolios.reduce((tags: Tag[], portfolio: PortfolioViewModel) => {
        if (portfolio.tags) {
            return tags.concat(portfolio.tags);
        }
        return tags;
    }, []);

    const uniqueKeys: Set<string> = allTags.reduce((keys: Set<string>, tag: Tag) => {
        keys.add(tag.key);
        return keys;
    }, new Set<string>());

    const columnDef: ColumnDefinition<PortfolioViewModel>[] = [];
    uniqueKeys.forEach(key => {
        columnDef.push({
            id: key,
            header: key,
            cell: item => item,
            data: item => item.tags?.find(tag => tag.key === key)?.value ?? '',
            type: ColumnTypes.TEXT
        });
    });
    return columnDef;
};
